
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  AlertTriangle, 
  CheckCircle, 
  Globe, 
  Search, 
  Download,
  Filter,
  Eye,
  Edit,
  Save
} from 'lucide-react';
import { 
  TranslationAnalysisReport, 
  ComponentAnalysis, 
  TranslationIssue,
  generateTranslationReport,
  suggestTranslationKey
} from '@/utils/translationAnalyzer';
import { Language, languageNames, languageFlags } from '@/context/LanguageContext';

// Datos simulados de componentes para demostración
const mockComponentsData = [
  {
    name: 'HeroSection',
    path: 'src/components/home/HeroSection.tsx',
    content: `
      export const HeroSection = () => {
        const { t } = useLanguage();
        return (
          <div>
            <h1>{t('heroTitle')}</h1>
            <p>Find the best therapists worldwide</p>
            <button>Get Started Today</button>
          </div>
        );
      };
    `
  },
  {
    name: 'ContactForm',
    path: 'src/components/contact/ContactForm.tsx',
    content: `
      export const ContactForm = () => {
        const { t } = useLanguage();
        return (
          <form>
            <label>{t('firstName')}</label>
            <label>Email Address</label>
            <button>{t('send')}</button>
            <p>We'll get back to you within 24 hours</p>
          </form>
        );
      };
    `
  }
];

const TranslationAnalyzer: React.FC = () => {
  const [report, setReport] = useState<TranslationAnalysisReport | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<ComponentAnalysis | null>(null);
  const [selectedIssue, setSelectedIssue] = useState<TranslationIssue | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSeverity, setFilterSeverity] = useState<'all' | 'high' | 'medium' | 'low'>('all');
  const [editingTranslations, setEditingTranslations] = useState<Record<string, string>>({});

  useEffect(() => {
    // Generar el reporte inicial
    const generatedReport = generateTranslationReport(mockComponentsData);
    setReport(generatedReport);
  }, []);

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const filteredComponents = report?.componentAnalysis.filter(component => {
    const matchesSearch = component.componentName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = filterSeverity === 'all' || 
      component.issues.some(issue => issue.severity === filterSeverity);
    return matchesSearch && matchesSeverity;
  }) || [];

  const handleEditTranslation = (key: string, language: Language, value: string) => {
    setEditingTranslations(prev => ({
      ...prev,
      [`${key}-${language}`]: value
    }));
  };

  const saveTranslation = (key: string, language: Language) => {
    const newValue = editingTranslations[`${key}-${language}`];
    console.log(`Saving translation: ${key} (${language}) = ${newValue}`);
    // Aquí iría la lógica para guardar la traducción
  };

  const exportReport = () => {
    if (!report) return;
    
    const reportData = JSON.stringify(report, null, 2);
    const blob = new Blob([reportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'translation-analysis-report.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!report) {
    return <div>Cargando análisis de traducciones...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Analizador de Traducciones
        </h1>
        <p className="text-gray-600 mb-6">
          Herramienta para identificar y corregir problemas de localización en toda la aplicación
        </p>

        {/* Resumen del reporte */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <Globe className="h-8 w-8 text-blue-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Componentes</p>
                  <p className="text-2xl font-bold">{report.totalComponents}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <CheckCircle className="h-8 w-8 text-green-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Claves de Traducción</p>
                  <p className="text-2xl font-bold">{report.totalTranslationKeys}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <AlertTriangle className="h-8 w-8 text-red-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Problemas</p>
                  <p className="text-2xl font-bold">{report.totalIssues}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center">
                <Edit className="h-8 w-8 text-orange-500 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Texto Hardcodeado</p>
                  <p className="text-2xl font-bold">{report.hardcodedTextInstances}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Controles */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-64">
            <Input
              placeholder="Buscar componentes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>
          <select 
            className="px-3 py-2 border border-gray-300 rounded-md"
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value as any)}
          >
            <option value="all">Todas las severidades</option>
            <option value="high">Severidad Alta</option>
            <option value="medium">Severidad Media</option>
            <option value="low">Severidad Baja</option>
          </select>
          <Button onClick={exportReport} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Exportar Reporte
          </Button>
        </div>
      </div>

      <Tabs defaultValue="components" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="components">Componentes</TabsTrigger>
          <TabsTrigger value="issues">Problemas</TabsTrigger>
          <TabsTrigger value="languages">Por Idioma</TabsTrigger>
        </TabsList>

        <TabsContent value="components" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lista de componentes */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Componentes Analizados</h3>
              {filteredComponents.map((component) => (
                <Card 
                  key={component.componentName}
                  className={`cursor-pointer transition-colors ${
                    selectedComponent?.componentName === component.componentName 
                      ? 'ring-2 ring-blue-500' 
                      : 'hover:bg-gray-50'
                  }`}
                  onClick={() => setSelectedComponent(component)}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold">{component.componentName}</h4>
                      <Badge variant="outline">
                        {component.issues.length} problemas
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{component.filePath}</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">
                        {component.translationKeys.length} claves
                      </Badge>
                      {component.hardcodedText.length > 0 && (
                        <Badge variant="destructive">
                          {component.hardcodedText.length} texto hardcodeado
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Detalles del componente seleccionado */}
            <div>
              {selectedComponent && (
                <Card>
                  <CardHeader>
                    <CardTitle>Detalles: {selectedComponent.componentName}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h5 className="font-medium mb-2">Claves de Traducción</h5>
                      <div className="space-y-1">
                        {selectedComponent.translationKeys.map((key) => (
                          <div key={key} className="text-sm bg-gray-50 p-2 rounded">
                            <code>{key}</code>
                          </div>
                        ))}
                      </div>
                    </div>

                    {selectedComponent.hardcodedText.length > 0 && (
                      <div>
                        <h5 className="font-medium mb-2 text-red-700">Texto Hardcodeado</h5>
                        <div className="space-y-2">
                          {selectedComponent.hardcodedText.map((text, index) => (
                            <div key={index} className="bg-red-50 p-2 rounded">
                              <p className="text-sm text-red-800">"{text}"</p>
                              <p className="text-xs text-red-600 mt-1">
                                Sugerencia: {suggestTranslationKey(text, selectedComponent.componentName)}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div>
                      <h5 className="font-medium mb-2">Problemas de Traducción</h5>
                      <div className="space-y-2">
                        {selectedComponent.issues.map((issue, index) => (
                          <div 
                            key={index} 
                            className={`p-3 rounded border ${getSeverityColor(issue.severity)}`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <code className="text-sm">{issue.key}</code>
                              <Badge className={getSeverityColor(issue.severity)}>
                                {issue.severity}
                              </Badge>
                            </div>
                            <p className="text-sm">
                              Idiomas faltantes: {issue.missingLanguages.join(', ')}
                            </p>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="mt-2"
                              onClick={() => setSelectedIssue(issue)}
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              Editar Traducciones
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="issues" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Editor de traducciones */}
            {selectedIssue && (
              <Card>
                <CardHeader>
                  <CardTitle>Editar: {selectedIssue.key}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {(Object.keys(languageNames) as Language[]).map((lang) => (
                      <div key={lang}>
                        <label className="flex items-center gap-2 mb-2">
                          <span className="text-lg">{languageFlags[lang]}</span>
                          <span className="font-medium">{languageNames[lang]}</span>
                          {selectedIssue.missingLanguages.includes(lang) && (
                            <Badge variant="destructive" className="text-xs">Faltante</Badge>
                          )}
                        </label>
                        <div className="flex gap-2">
                          <Textarea
                            value={editingTranslations[`${selectedIssue.key}-${lang}`] || 
                                   selectedIssue.currentTranslations[lang] || ''}
                            onChange={(e) => handleEditTranslation(selectedIssue.key, lang, e.target.value)}
                            placeholder={`Traducción en ${languageNames[lang]}...`}
                            className="flex-1"
                          />
                          <Button 
                            size="sm"
                            onClick={() => saveTranslation(selectedIssue.key, lang)}
                            disabled={!editingTranslations[`${selectedIssue.key}-${lang}`]}
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="languages" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(Object.keys(report.missingTranslationsByLanguage) as Language[]).map((lang) => (
              <Card key={lang}>
                <CardContent className="p-4">
                  <div className="flex items-center mb-3">
                    <span className="text-2xl mr-2">{languageFlags[lang]}</span>
                    <div>
                      <h4 className="font-semibold">{languageNames[lang]}</h4>
                      <p className="text-sm text-gray-600">
                        {report.missingTranslationsByLanguage[lang]} traducciones faltantes
                      </p>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ 
                        width: `${Math.max(0, 100 - (report.missingTranslationsByLanguage[lang] / report.totalTranslationKeys * 100))}%` 
                      }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {Math.round(Math.max(0, 100 - (report.missingTranslationsByLanguage[lang] / report.totalTranslationKeys * 100)))}% completo
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TranslationAnalyzer;
